import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Settings,
  X,
  GripVertical,
  Save,
  AlertCircle,
  Check,
  Lock,
  Unlock,
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import toast from 'react-hot-toast';
import { setoresAPI } from '../services/api';

const AdminSetores = () => {
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showSetorModal, setShowSetorModal] = useState(false);
  const [showColunasModal, setShowColunasModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingSetor, setEditingSetor] = useState(null);
  const [deletingSetor, setDeletingSetor] = useState(null);
  const [selectedSetorColunas, setSelectedSetorColunas] = useState([]);

  // Form states
  const [setorForm, setSetorForm] = useState({
    nome: '',
    descricao: '',
    cor: 'var(--brand, #20a490)',
    sla_normal_horas: 24,
    sla_alta_horas: 8,
    sla_urgente_horas: 2,
  });

  const [colunaForm, setColunaForm] = useState({
    nome: '',
    cor_fundo: '#ffffff',
    eh_entrada: false,
    eh_final: false,
  });
  const [editingColuna, setEditingColuna] = useState(null);
  const [showColunaForm, setShowColunaForm] = useState(false);

  useEffect(() => {
    loadSetores();
  }, []);

  const loadSetores = async () => {
    setLoading(true);
    try {
      const response = await setoresAPI.list(false); // Buscar todos, incluindo inativos
      setSetores(response.data);
    } catch (error) {
      toast.error('Erro ao carregar setores');
    }
    setLoading(false);
  };

  // --- Setores ---
  const handleOpenSetorModal = (setor = null) => {
    if (setor) {
      setEditingSetor(setor);
      setSetorForm({
        nome: setor.nome,
        descricao: setor.descricao || '',
        cor: setor.cor,
        sla_normal_horas: setor.sla_normal_horas,
        sla_alta_horas: setor.sla_alta_horas,
        sla_urgente_horas: setor.sla_urgente_horas,
      });
    } else {
      setEditingSetor(null);
      setSetorForm({
        nome: '',
        descricao: '',
        cor: 'var(--brand, #20a490)',
        sla_normal_horas: 24,
        sla_alta_horas: 8,
        sla_urgente_horas: 2,
      });
    }
    setShowSetorModal(true);
  };

  const handleSaveSetor = async () => {
    if (!setorForm.nome) {
      toast.error('Nome do setor é obrigatório');
      return;
    }

    try {
      if (editingSetor) {
        await setoresAPI.update(editingSetor.id, setorForm);
        toast.success('Setor atualizado com sucesso');
      } else {
        await setoresAPI.create(setorForm);
        toast.success('Setor criado com sucesso');
      }
      setShowSetorModal(false);
      loadSetores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar setor');
    }
  };

  const handleDeleteSetor = async () => {
    try {
      await setoresAPI.delete(deletingSetor.id);
      toast.success('Setor excluído com sucesso');
      setShowDeleteModal(false);
      setDeletingSetor(null);
      loadSetores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir setor');
    }
  };

  const handleToggleAtivo = async (setor) => {
    try {
      await setoresAPI.update(setor.id, { ativo: !setor.ativo });
      toast.success(setor.ativo ? 'Setor desativado' : 'Setor ativado');
      loadSetores();
    } catch (error) {
      toast.error('Erro ao alterar status do setor');
    }
  };

  const handleToggleCadeado = async (setor) => {
    try {
      await setoresAPI.update(setor.id, { cadeado: !setor.cadeado });
      toast.success(setor.cadeado ? 'Cadeado removido' : 'Cadeado adicionado');
      loadSetores();
    } catch (error) {
      toast.error('Erro ao alterar cadeado');
    }
  };

  // --- Colunas ---
  const handleOpenColunasModal = async (setor) => {
    try {
      const response = await setoresAPI.getColunas(setor.id);
      setSelectedSetorColunas(response.data);
      setEditingSetor(setor);
      setShowColunaForm(false);
      setEditingColuna(null);
      setShowColunasModal(true);
    } catch (error) {
      toast.error('Erro ao carregar colunas');
    }
  };

  const handleOpenColunaForm = (coluna = null) => {
    if (coluna) {
      setEditingColuna(coluna);
      setColunaForm({
        nome: coluna.nome,
        cor_fundo: coluna.cor_fundo,
        eh_entrada: coluna.eh_entrada,
        eh_final: coluna.eh_final,
      });
    } else {
      setEditingColuna(null);
      setColunaForm({
        nome: '',
        cor_fundo: '#ffffff',
        eh_entrada: false,
        eh_final: false,
      });
    }
    setShowColunaForm(true);
  };

  const handleSaveColuna = async () => {
    if (!colunaForm.nome) {
      toast.error('Nome da coluna é obrigatório');
      return;
    }

    try {
      if (editingColuna) {
        await setoresAPI.updateColuna(editingColuna.id, colunaForm);
        toast.success('Coluna atualizada');
      } else {
        await setoresAPI.createColuna(editingSetor.id, {
          ...colunaForm,
          setor_id: editingSetor.id,
          ordem: selectedSetorColunas.length,
        });
        toast.success('Coluna criada');
      }
      // Recarregar colunas
      const response = await setoresAPI.getColunas(editingSetor.id);
      setSelectedSetorColunas(response.data);
      setShowColunaForm(false);
      setEditingColuna(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar coluna');
    }
  };

  const handleDeleteColuna = async (colunaId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta coluna?')) return;

    try {
      await setoresAPI.deleteColuna(colunaId);
      toast.success('Coluna excluída');
      const response = await setoresAPI.getColunas(editingSetor.id);
      setSelectedSetorColunas(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir coluna');
    }
  };

  const handleDragStartColunas = (start) => {
    console.log('[DEBUG] handleDragStartColunas chamado', start);
  };

  const handleDragEndColunas = async (result) => {
    console.log('[DEBUG] handleDragEndColunas chamado', result);

    if (!result.destination) {
      console.log('[DEBUG] Sem destino, ignorando');
      return;
    }

    console.log('[DEBUG] Source:', result.source.index, 'Destination:', result.destination.index);

    const items = Array.from(selectedSetorColunas);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    console.log('[DEBUG] Nova ordem:', items.map(c => ({ id: c.id, nome: c.nome })));
    setSelectedSetorColunas(items);

    // Salvar nova ordem no backend
    try {
      const colunaIds = items.map((c) => c.id);
      console.log('[DEBUG] Enviando colunaIds:', colunaIds);
      await setoresAPI.reordenarColunas(editingSetor.id, colunaIds);
      console.log('[DEBUG] Reordenação salva com sucesso');
    } catch (error) {
      console.error('[DEBUG] Erro ao reordenar:', error);
      toast.error('Erro ao reordenar colunas');
      // Recarregar colunas originais
      const response = await setoresAPI.getColunas(editingSetor.id);
      setSelectedSetorColunas(response.data);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div className="search-box" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ color: 'var(--gray-800)' }}>Gerenciar Setores</h2>
          <button className="btn btn-primary" onClick={() => handleOpenSetorModal()}>
            <Plus size={18} />
            Novo Setor
          </button>
        </div>
      </div>

      {/* Lista de Setores */}
      <div className="setores-list">
        {setores.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} />
            <p>Nenhum setor cadastrado</p>
          </div>
        ) : (
          setores.map((setor) => (
            <div key={setor.id} className={`setor-card ${!setor.ativo ? 'inativo' : ''}`}>
              <div className="setor-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div
                    className="setor-cor"
                    style={{ backgroundColor: setor.cor }}
                  />
                  <div>
                    <h3 style={{ margin: 0, color: 'var(--gray-800)' }}>
                      {setor.nome}
                      {!setor.ativo && (
                        <span
                          style={{
                            fontSize: '0.75rem',
                            background: 'var(--gray-300)',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            marginLeft: '0.5rem',
                          }}
                        >
                          Inativo
                        </span>
                      )}
                    </h3>
                    {setor.descricao && (
                      <p style={{ margin: '0.25rem 0 0', color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                        {setor.descricao}
                      </p>
                    )}
                  </div>
                </div>
                <div className="setor-actions">
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '0.5rem' }}
                    onClick={() => handleToggleCadeado(setor)}
                    title={setor.cadeado ? 'Remover cadeado' : 'Adicionar cadeado'}
                  >
                    {setor.cadeado ? <Lock size={16} /> : <Unlock size={16} />}
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '0.5rem' }}
                    onClick={() => handleToggleAtivo(setor)}
                    title={setor.ativo ? 'Desativar' : 'Ativar'}
                  >
                    {setor.ativo ? <Check size={16} /> : <X size={16} />}
                  </button>
                  <button
                    className="btn btn-primary"
                    style={{ padding: '0.5rem' }}
                    onClick={() => handleOpenColunasModal(setor)}
                    title="Gerenciar colunas"
                  >
                    <Settings size={16} />
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '0.5rem' }}
                    onClick={() => handleOpenSetorModal(setor)}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="btn btn-danger"
                    style={{ padding: '0.5rem' }}
                    onClick={() => {
                      setDeletingSetor(setor);
                      setShowDeleteModal(true);
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="setor-sla">
                <span>SLA Normal: <strong>{setor.sla_normal_horas}h</strong></span>
                <span>Alta: <strong>{setor.sla_alta_horas}h</strong></span>
                <span>Urgente: <strong>{setor.sla_urgente_horas}h</strong></span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Criar/Editar Setor */}
      {showSetorModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>{editingSetor ? 'Editar Setor' : 'Novo Setor'}</h3>
              <button className="modal-close" onClick={() => setShowSetorModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="form-group">
              <label>Nome *</label>
              <input
                type="text"
                className="form-control"
                value={setorForm.nome}
                onChange={(e) => setSetorForm({ ...setorForm, nome: e.target.value })}
                placeholder="Ex: Atendimento, Financeiro..."
              />
            </div>

            <div className="form-group">
              <label>Descrição</label>
              <textarea
                className="form-control"
                rows="2"
                value={setorForm.descricao}
                onChange={(e) => setSetorForm({ ...setorForm, descricao: e.target.value })}
                placeholder="Descrição do setor..."
              />
            </div>

            <div className="form-group">
              <label>Cor</label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <input
                  type="color"
                  value={setorForm.cor}
                  onChange={(e) => setSetorForm({ ...setorForm, cor: e.target.value })}
                  style={{ width: '50px', height: '40px', border: 'none', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  className="form-control"
                  value={setorForm.cor}
                  onChange={(e) => setSetorForm({ ...setorForm, cor: e.target.value })}
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>SLA Normal (h)</label>
                <input
                  type="number"
                  className="form-control"
                  value={setorForm.sla_normal_horas}
                  onChange={(e) =>
                    setSetorForm({ ...setorForm, sla_normal_horas: parseInt(e.target.value) || 0 })
                  }
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>SLA Alta (h)</label>
                <input
                  type="number"
                  className="form-control"
                  value={setorForm.sla_alta_horas}
                  onChange={(e) =>
                    setSetorForm({ ...setorForm, sla_alta_horas: parseInt(e.target.value) || 0 })
                  }
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>SLA Urgente (h)</label>
                <input
                  type="number"
                  className="form-control"
                  value={setorForm.sla_urgente_horas}
                  onChange={(e) =>
                    setSetorForm({ ...setorForm, sla_urgente_horas: parseInt(e.target.value) || 0 })
                  }
                  min="1"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowSetorModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSaveSetor}>
                <Save size={16} />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Colunas */}
      {showColunasModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Colunas - {editingSetor?.nome}</h3>
              <button className="modal-close" onClick={() => setShowColunasModal(false)}>
                <X size={20} />
              </button>
            </div>

            {!showColunaForm ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <button className="btn btn-primary" onClick={() => handleOpenColunaForm()}>
                    <Plus size={16} />
                    Nova Coluna
                  </button>
                </div>

                <DragDropContext onDragStart={handleDragStartColunas} onDragEnd={handleDragEndColunas}>
                  <Droppable droppableId="colunas">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="colunas-list"
                      >
                        {selectedSetorColunas.map((coluna, index) => (
                          <Draggable
                            key={coluna.id}
                            draggableId={coluna.id.toString()}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="coluna-item"
                                style={{
                                  ...provided.draggableProps.style,
                                  backgroundColor: coluna.cor_fundo,
                                }}
                              >
                                <div {...provided.dragHandleProps} className="drag-handle">
                                  <GripVertical size={18} />
                                </div>
                                <div className="coluna-info">
                                  <span className="coluna-nome">{coluna.nome}</span>
                                  <div className="coluna-badges">
                                    {coluna.eh_entrada && (
                                      <span className="badge badge-entrada">ENTRADA</span>
                                    )}
                                    {coluna.eh_final && (
                                      <span className="badge badge-final">FINAL</span>
                                    )}
                                  </div>
                                </div>
                                <div className="coluna-actions">
                                  <button
                                    className="btn btn-secondary"
                                    style={{ padding: '0.4rem' }}
                                    onClick={() => handleOpenColunaForm(coluna)}
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    className="btn btn-danger"
                                    style={{ padding: '0.4rem' }}
                                    onClick={() => handleDeleteColuna(coluna.id)}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </>
            ) : (
              <div style={{ padding: '1rem 0' }}>
                <h4 style={{ marginBottom: '1rem' }}>
                  {editingColuna ? 'Editar Coluna' : 'Nova Coluna'}
                </h4>

                <div className="form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={colunaForm.nome}
                    onChange={(e) => setColunaForm({ ...colunaForm, nome: e.target.value })}
                    placeholder="Ex: Aguardando Atendimento"
                  />
                </div>

                <div className="form-group">
                  <label>Cor de Fundo</label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={colunaForm.cor_fundo}
                      onChange={(e) => setColunaForm({ ...colunaForm, cor_fundo: e.target.value })}
                      style={{ width: '50px', height: '40px', border: 'none', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      className="form-control"
                      value={colunaForm.cor_fundo}
                      onChange={(e) => setColunaForm({ ...colunaForm, cor_fundo: e.target.value })}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={colunaForm.eh_entrada}
                      onChange={(e) =>
                        setColunaForm({ ...colunaForm, eh_entrada: e.target.checked, eh_final: false })
                      }
                    />
                    Coluna de Entrada
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={colunaForm.eh_final}
                      onChange={(e) =>
                        setColunaForm({ ...colunaForm, eh_final: e.target.checked, eh_entrada: false })
                      }
                    />
                    Coluna Final
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowColunaForm(false);
                      setEditingColuna(null);
                    }}
                  >
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveColuna}>
                    <Save size={16} />
                    Salvar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Confirmar Exclusão */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Confirmar Exclusão</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <X size={20} />
              </button>
            </div>
            <p style={{ marginBottom: '1.5rem' }}>
              Tem certeza que deseja excluir o setor <strong>{deletingSetor?.nome}</strong>?
              <br />
              <small style={{ color: 'var(--danger)' }}>
                Esta ação não pode ser desfeita.
              </small>
            </p>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={handleDeleteSetor}>
                <Trash2 size={16} />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSetores;
